import useFormValidation from "../../hooks/useFormValidation";
import Button from "../../components/ui/Button";
import Modal from "../../components/ui/Modal";

import React, { useEffect, useState } from "react";
import ProductImageFields from "../ProductImageFields/ProductImageFields";
import { appendProductImages, ProductImageSelection } from "../../utils/productImages";
import "./ProductFormModal.css";
import {
  PRODUCT_STATUS,

} from "../../constants/productStatus";

export interface ProductFormInitialValues {
  name: string;
  price: string | number;
  quantity: string | number;
  description: string;
  productStatus: string;
  image?: string[];
}

interface ModalProps {
  onClose: () => void;
  onSave: (formData: FormData) => void;
  loading: boolean;
  mode?: "create" | "edit";
  initialValues?: ProductFormInitialValues;
}

const ProductFormModal: React.FC<ModalProps> = ({
  onClose,
  onSave,
  loading,
  mode = "create",
  initialValues,
}) => {
  const { errors, validate, clearError } = useFormValidation();
  const isEdit = mode === "edit";

  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [quantity, setQuantity] = useState("");
  const [description, setDescription] = useState("");
  const productStatus = Number(quantity) > 0 ? PRODUCT_STATUS.IN_STOCK : PRODUCT_STATUS.OUT_OF_STOCK;
  const [images, setImages] = useState<ProductImageSelection>({ mode: isEdit ? "keep" : "all", files: [], replaceImageUrl: "" });
  const [imageError, setImageError] = useState("");

  useEffect(() => {
    if (!initialValues) return;
    setName(initialValues.name);
    setPrice(String(initialValues.price));
    setQuantity(String(initialValues.quantity));
    setDescription(initialValues.description || "");
    setImages({ mode: isEdit ? "keep" : "all", files: [], replaceImageUrl: "" });
  }, [initialValues, isEdit]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate(e.currentTarget as HTMLFormElement)) return;
    const formData = new FormData();
    formData.append("name", name);
    formData.append("price", price);
    formData.append("quantity", quantity);
    formData.append("description", description);
    formData.append("productStatus", productStatus);

    setImageError("");
    try { appendProductImages(formData, images, initialValues?.image); }
    catch (err) { setImageError(err instanceof Error ? err.message : "Please check the selected images."); return; }

    onSave(formData);
  };

  return (
    <Modal dismissible={!loading} description="Set product details, stock quantity and images." footer={<><Button disabled={loading} onClick={onClose}>Cancel</Button><Button variant="primary" type="submit" form="product-editor" disabled={loading}>{loading ? "Saving…" : isEdit ? "Update Product" : "Save Product"}</Button></>} title={<>{isEdit ? "Edit Product" : "Add New Product"}</>} onClose={onClose} size="medium">
<div className="product-form-modal legacy-modal-content" onClick={(e) => e.stopPropagation()}>

        <form noValidate id="product-editor" onSubmit={handleSubmit} className="modal-form-content">
          <div className="form-scroll-container">
            <div className="form-group">
              <label htmlFor="product-name">Product Name</label>
              <input
                id="product-name" name="name" aria-invalid={!!errors.name} aria-describedby={errors.name ? "product-name-error" : undefined} type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); clearError("name"); }}
                required
              />
                {errors.name && <p id="product-name-error" className="navo-field-error" role="alert">{errors.name}</p>}
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="product-price">Price (NGN)</label>
                <input
                  id="product-price" name="price" aria-invalid={!!errors.price} aria-describedby={errors.price ? "product-price-error" : undefined} type="number"
                  min="0.01"
                  step="0.01"
                  value={price}
                  onChange={(e) => { setPrice(e.target.value); clearError("price"); }}
                  required
                />
                {errors.price && <p id="product-price-error" className="navo-field-error" role="alert">{errors.price}</p>}
              </div>
              <div className="form-group">
                <label htmlFor="product-quantity">Quantity</label>
                <input
                  type="number"
                  id="product-quantity" name="quantity" aria-invalid={!!errors.quantity} aria-describedby={errors.quantity ? "product-quantity-error" : undefined} step="1" min="0"
                  value={quantity}
                  onChange={(e) => { setQuantity(e.target.value); clearError("quantity"); }}
                  required
                />
                {errors.quantity && <p id="product-quantity-error" className="navo-field-error" role="alert">{errors.quantity}</p>}
              </div>
            </div>
            <div className="product-stock-summary" role="status">
              <strong>{Number(quantity) > 0 ? "In Stock" : "Out of Stock"}</strong>
              <p>Stock status is calculated from quantity. Add stock to make this product available.</p>
            </div>
            <div className="form-group">
              <label htmlFor="product-description">Description</label>
              <textarea id="product-description" name="description" aria-invalid={!!errors.description} aria-describedby={errors.description ? "product-description-error" : undefined}
                value={description}
                onChange={(e) => { setDescription(e.target.value); clearError("description"); }}
                rows={4}
                required
              />
                {errors.description && <p id="product-description-error" className="navo-field-error" role="alert">{errors.description}</p>}
            </div>
            {imageError && <p className="integration-error" role="alert">{imageError}</p>}
            <ProductImageFields value={images} onChange={setImages} existing={initialValues?.image} creating={!isEdit} disabled={loading} />
          </div>


        </form>
      </div>
</Modal>
  );
};

export default ProductFormModal;
